import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import EmailPasswordLoginForm from '@/components/forms/email-password-login-form'
import { AuthSplitLayout } from '@/components/layout/auth-split-layout'
import { Button } from '@/components/ui/button'
import { FieldDescription } from '@/components/ui/field'
import authenticationService from '@/services/authentication/authentication'

export const Route = createFileRoute('/login/')({
  component: LoginPage,
})

function LoginPage() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError(undefined)

    try {
      await authenticationService.signInWithGoogle()
    } catch (err: unknown) {
      console.error('Google Sign In Error:', err)
      setError(t('loginPage.googleSignInError'))
    } finally {
      setLoading(false)
    }
  }

  const handleAppleSignIn = async () => {
    setLoading(true)
    setError(undefined)

    try {
      await authenticationService.signInWithApple()
    } catch (err: unknown) {
      console.error('Apple Sign In Error:', err)
      setError(t('loginPage.appleSignInError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthSplitLayout>
      <div className="flex-1 flex flex-col justify-center w-full md:flex-none md:mb-6 md:space-y-6 ">
        <div className="flex flex-col gap-y-3">
          <h1 className="heading-1 text-center">{t('landingPage.title')}</h1>
          <p className="body-1 text-text-secondary text-center">{t('landingPage.description')}</p>
        </div>
      </div>

      <div className="w-full gap-3 flex flex-col">
        <EmailPasswordLoginForm />

        <div className="text-center lowercase text-text-secondary">{t('global.or')}</div>

        <Button
          onClick={handleGoogleSignIn}
          type="button"
          disabled={loading}
          className="w-full bg-white border border-gray-300 text-gray-700 font-bold py-2 h-11 px-4 rounded-md hover:bg-gray-50 flex items-center justify-center"
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            width={20}
            height={20}
            className="mr-3"
            alt="Google"
          />
          {t('loginPage.googleSignInButton')}
        </Button>

        <Button
          onClick={handleAppleSignIn}
          type="button"
          disabled={loading}
          className="w-full bg-black border border-black text-white font-bold py-2 h-11 px-4 rounded-md hover:bg-gray-900 flex items-center justify-center"
        >
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="currentColor"
            className="mr-3"
            aria-hidden="true"
          >
            <path d="M16.365 1.43c0 1.14-.42 2.2-1.12 3-.79.92-2.07 1.63-3.11 1.55a3.4 3.4 0 0 1-.02-.42c0-1.1.47-2.24 1.16-2.98.77-.85 2.1-1.5 3.06-1.53.02.13.03.26.03.38zM20.9 17.3c-.5 1.14-.74 1.65-1.38 2.66-.9 1.41-2.16 3.17-3.73 3.18-1.39.01-1.75-.9-3.64-.89-1.89.01-2.28.91-3.68.9-1.57-.02-2.77-1.6-3.66-3.01-2.5-3.95-2.76-8.58-1.22-11.04 1.1-1.75 2.83-2.77 4.46-2.77 1.66 0 2.7.91 4.07.91 1.33 0 2.14-.91 4.06-.91 1.45 0 2.99.79 4.09 2.16-3.6 1.97-3.02 7.11.63 8.81z" />
          </svg>
          {t('loginPage.appleSignInButton')}
        </Button>
        {error && <FieldDescription>{error}</FieldDescription>}

        <p className="text-text-secondary text-center body-2">
          {t('loginPage.noAccountLabel')}{' '}
          <Link to="/register-account" className="text-link-primary underline font-semibold">
            {t('loginPage.createAccountLink')}
          </Link>
        </p>
      </div>
    </AuthSplitLayout>
  )
}
